import React, { useContext, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, { categories } from "../Datas/productData";
import { formatPrice } from "../Datas/siteConfig";
import "../css/shop.scss";

const sortOptions = [
  { value: "featured", label: "Featured" },
  { value: "name-asc", label: "Alphabetically, A–Z" },
  { value: "price-asc", label: "Price, low to high" },
  { value: "price-desc", label: "Price, high to low" },
];

function Shop() {
  const { addToCart, toggleWishlist, isWishlisted } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCategory = searchParams.get("category");
  const category = categories.includes(requestedCategory) ? requestedCategory : "All";
  const [sortBy, setSortBy] = useState("featured");
  const query = searchParams.get("q") || "";
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const availableColors = useMemo(
    () => [...new Set(productData.flatMap((product) => product.colors))].sort(),
    []
  );
  const availableSizes = useMemo(
    () => [...new Set(productData.flatMap((product) => product.sizes))],
    []
  );

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const result = productData.filter((product) => {
      const categoryMatch = category === "All" || product.category === category;
      const queryMatch =
        !normalizedQuery ||
        `${product.name} ${product.category} ${product.tags.join(" ")}`
          .toLowerCase()
          .includes(normalizedQuery);
      const colorMatch =
        selectedColors.length === 0 ||
        selectedColors.some((color) => product.colors.includes(color));
      const sizeMatch =
        selectedSizes.length === 0 ||
        selectedSizes.some((size) => product.sizes.includes(size));
      return categoryMatch && queryMatch && colorMatch && sizeMatch;
    });

    return [...result].sort((a, b) => {
      if (sortBy === "name-asc") return a.name.localeCompare(b.name);
      if (sortBy === "price-asc") return a.price - b.price;
      if (sortBy === "price-desc") return b.price - a.price;
      return Number(b.featured) - Number(a.featured) || a.id - b.id;
    });
  }, [category, query, selectedColors, selectedSizes, sortBy]);

  const updateParam = (name, value, emptyValue = "") => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value || value === emptyValue) nextParams.delete(name);
    else nextParams.set(name, value);
    setSearchParams(nextParams, { replace: true });
  };

  const toggleFilter = (value, setter) => {
    setter((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
  };

  const clearFilters = () => {
    setSearchParams({}, { replace: true });
    setSelectedColors([]);
    setSelectedSizes([]);
  };

  return (
    <main className="shop-page" id="main-content">
      <header className="shop-page__header">
        <p className="eyebrow">Wearable art, made in small editions</p>
        <h1>{category === "All" ? "Shop all" : category}</h1>
      </header>

      <div className="shop-toolbar">
        <button
          type="button"
          className="shop-toolbar__filter-button"
          onClick={() => setFilterOpen(true)}
          aria-expanded={filterOpen}
          aria-controls="shop-filters"
        >
          Filter
          {(selectedColors.length > 0 || selectedSizes.length > 0) && (
            <span> ({selectedColors.length + selectedSizes.length})</span>
          )}
        </button>
        <p aria-live="polite">{filteredProducts.length} products</p>
        <label>
          <span>Sort by:</span>
          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            aria-label="Sort products"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        className={`shop-filter-backdrop${filterOpen ? " is-open" : ""}`}
        onClick={() => setFilterOpen(false)}
        aria-hidden="true"
      />
      <aside
        id="shop-filters"
        className={`shop-filters${filterOpen ? " is-open" : ""}`}
        aria-hidden={!filterOpen}
      >
        <div className="shop-filters__heading">
          <h2>Filter and sort</h2>
          <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters">
            ×
          </button>
        </div>

        <label className="shop-filters__search">
          <span>Search</span>
          <input
            type="search"
            value={query}
            onChange={(event) => updateParam("q", event.target.value)}
            placeholder="Search hats, dresses, prints…"
          />
        </label>

        <fieldset>
          <legend>Category</legend>
          {categories.map((item) => (
            <label key={item}>
              <input
                type="radio"
                name="category"
                checked={category === item}
                onChange={() => updateParam("category", item, "All")}
              />
              <span>{item}</span>
            </label>
          ))}
        </fieldset>

        <fieldset>
          <legend>Colour</legend>
          <div className="shop-filters__grid">
            {availableColors.map((color) => (
              <label key={color}>
                <input
                  type="checkbox"
                  checked={selectedColors.includes(color)}
                  onChange={() => toggleFilter(color, setSelectedColors)}
                />
                <span>{color}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend>Size</legend>
          <div className="shop-filters__grid">
            {availableSizes.map((size) => (
              <label key={size}>
                <input
                  type="checkbox"
                  checked={selectedSizes.includes(size)}
                  onChange={() => toggleFilter(size, setSelectedSizes)}
                />
                <span>{size}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="shop-filters__actions">
          <button type="button" className="button button--outline" onClick={clearFilters}>
            Remove all
          </button>
          <button type="button" className="button" onClick={() => setFilterOpen(false)}>
            Show {filteredProducts.length}
          </button>
        </div>
      </aside>

      {filteredProducts.length > 0 ? (
        <section className="shop-grid" aria-label="Products">
          {filteredProducts.map((product) => (
            <article className="product-card" key={product.id}>
              <div className="product-card__media">
                <Link to={`/shopDetails/${product.id}`} aria-label={`View ${product.name}`}>
                  <img src={product.image} alt={product.name} loading="lazy" />
                </Link>
                {product.compareAtPrice && <span className="product-card__badge">Sale</span>}
                <button
                  type="button"
                  className={`product-card__wish${
                    isWishlisted(product.id) ? " is-active" : ""
                  }`}
                  onClick={() => toggleWishlist(product.id)}
                  aria-label={`${isWishlisted(product.id) ? "Remove" : "Add"} ${
                    product.name
                  } ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
                  aria-pressed={isWishlisted(product.id)}
                >
                  <span aria-hidden="true">♡</span>
                </button>
                <button
                  type="button"
                  className="product-card__quick-add"
                  onClick={() =>
                    addToCart(product.id, 1, product.sizes[0], product.colors[0])
                  }
                >
                  {product.sizes.length > 1 ? "Choose first option" : "Add to bag"}
                </button>
              </div>
              <div className="product-card__info">
                <h2>
                  <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                </h2>
                <div className="product-card__price">
                  <span>{formatPrice(product.price)}</span>
                  {product.compareAtPrice && <s>{formatPrice(product.compareAtPrice)}</s>}
                </div>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <section className="shop-empty" aria-live="polite">
          <h2>No pieces found</h2>
          <p>Try removing a filter or searching for another painted story.</p>
          <button type="button" className="button" onClick={clearFilters}>
            Clear filters
          </button>
        </section>
      )}
    </main>
  );
}

export default Shop;
